import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import { GENERATE_BROADCAST_CHANNEL_NAME } from './broadcast-channel.constant';
import { BroadcastChannelContext } from './broadcast-channel.context';
import {
  BROADCAST_CHANNEL_EVENTS,
  BroadcastChannelState,
  FctBroadcastChannelMessage,
  WindowOpenFunction,
} from './broadcast-channel.model';

export interface BroadcastChannelProviderProps {
  children: ReactNode;
  studyId: string;
}

export function BroadcastChannelProvider({ children, studyId }: BroadcastChannelProviderProps) {
  const trackedWindowsRef = useRef<Window[]>([]);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const channelNameRef = useRef<null | string>(null);

  const removeClosedWindows = () => {
    trackedWindowsRef.current = trackedWindowsRef.current.filter((win) => !win.closed);
  };

  const handleOpenTrackedWindow: WindowOpenFunction = useCallback((url, target, features) => {
    removeClosedWindows();

    const openedWindow = globalThis.open(url, target, features);

    if (openedWindow) {
      trackedWindowsRef.current.push(openedWindow);
    }

    return openedWindow;
  }, []);

  useEffect(() => {
    if (!('BroadcastChannel' in globalThis)) {
      return undefined;
    }

    const channelName = GENERATE_BROADCAST_CHANNEL_NAME(studyId);

    // Only recreate channel if name changed
    if (channelNameRef.current !== channelName) {
      if (channelRef.current) {
        channelRef.current.close();
      }

      channelRef.current = new BroadcastChannel(channelName);
      channelNameRef.current = channelName;
    }

    const closeTrackedWindows = () => {
      removeClosedWindows();

      trackedWindowsRef.current.forEach((win) => {
        if (!win.closed) {
          try {
            win.close();
          } catch {
            // Ignore errors when closing windows from different origins or already closed
          }
        }
      });

      trackedWindowsRef.current = [];
    };

    const handleBeforeUnload = () => {
      closeTrackedWindows();

      try {
        channelRef.current?.postMessage({
          studyId,
          type: BROADCAST_CHANNEL_EVENTS.CLOSE_FCT,
        });
      } catch {
        // Ignore errors if channel is already closed
      }
    };

    const handleChannelMessage = (event: MessageEvent<FctBroadcastChannelMessage>) => {
      if (
        event.data.type === BROADCAST_CHANNEL_EVENTS.CLOSE_FCT &&
        event.data.studyId === studyId
      ) {
        closeTrackedWindows();
      }
    };

    channelRef.current?.addEventListener('message', handleChannelMessage);
    globalThis.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      closeTrackedWindows();

      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
      channelRef.current?.removeEventListener('message', handleChannelMessage);

      if (channelRef.current) {
        try {
          channelRef.current.close();
        } catch {
          // Ignore errors if channel is already closed
        }
      }
    };
  }, [studyId]);

  const value: BroadcastChannelState = useMemo(
    () => ({
      onOpenTrackedWindow: handleOpenTrackedWindow,
    }),
    [handleOpenTrackedWindow],
  );

  return <BroadcastChannelContext value={value}>{children}</BroadcastChannelContext>;
}
